import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Upload, FileSpreadsheet, ScanSearch, Wand2 } from "lucide-react";
import { MONTHS_IT, fmtHoursSigned } from "@/lib/constants";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import { useEffectiveRole } from "@/foundation/context";

export default function Reconciliation() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useCompanySelection();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  useEffect(() => {
    if (!["admin", "super_admin"].includes(effectiveRole)) return;
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const sorted = sortCompaniesIt(data);
        setCompanies(sorted);
        if (!companyId && sorted.length) setCompanyId(sorted[0].id);
      } catch (e) { toast.error(apiErr(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const run = async () => {
    if (!file || !companyId) { toast.error("Seleziona file e azienda"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reconciliation/tabulato", fd, {
        params: { company_id: companyId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const applyCorrections = async () => {
    if (!file || !companyId) { toast.error("Seleziona file e azienda"); return; }
    if (!window.confirm("Applica le regole del tabulato ufficiale come verità?\n\nQuesta operazione aggiornerà i profili orari e riscrive le presenze dei mesi presenti nel tabulato. Le annotazioni manuali NON presenti nel tabulato saranno perse.")) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reconciliation/apply-tabulato", fd, {
        params: { company_id: companyId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setApplyResult(data);
      toast.success(`Riconciliazione applicata: ${data.employees_matched} dipendenti, ${data.attendance_updated + data.attendance_created} giorni allineati`);
      // Rilancia il confronto automaticamente
      await run();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setApplying(false); }
  };

  return (
    <div className="space-y-6 max-w-[1600px]">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Validazione motore di calcolo</div>
        <h1 className="text-3xl font-bold tracking-tight mt-1 text-slate-900" style={{ fontFamily: "Manrope" }}>
          Riconciliazione con Tabulato ufficiale
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Carica il tabulato Excel ufficiale (già validato) e l'app confronta ogni saldo mensile con quello calcolato.
          Per ogni differenza viene indicata la causa (annotazioni, arrotondamenti, profilo, ecc.).
        </p>
      </div>

      <Card className="border-slate-200 rounded-md">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            {["admin", "super_admin"].includes(effectiveRole) && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">Azienda</Label>
                <Select value={companyId || ""} onValueChange={setCompanyId}>
                  <SelectTrigger className="w-72" data-testid="rec-company-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="border-2 border-dashed border-slate-300 rounded-md p-6 text-center bg-slate-50 hover:bg-slate-100 cursor-pointer"
               onClick={() => document.getElementById("rec-input").click()}
               data-testid="rec-dropzone">
            <FileSpreadsheet size={32} className="mx-auto text-emerald-700 mb-2" />
            <div className="text-sm font-semibold text-slate-800">{file ? file.name : "Trascina/clicca per caricare il tabulato ufficiale"}</div>
            <input id="rec-input" type="file" accept=".xlsx" className="hidden"
                   onChange={(e) => setFile(e.target.files?.[0])} data-testid="rec-file-input" />
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={applyCorrections} disabled={!file || !companyId || applying || loading}
                    variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="rec-apply-btn">
              <Wand2 size={16} className="mr-2" /> {applying ? "Applicazione in corso…" : "Allinea al Tabulato (verità)"}
            </Button>
            <Button onClick={run} disabled={!file || !companyId || loading}
                    className="bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="rec-run-btn">
              <ScanSearch size={16} className="mr-2" /> {loading ? "Confronto in corso…" : "Confronta con app"}
            </Button>
          </div>
          {applyResult && (
            <div className="border border-emerald-200 rounded-md p-3 bg-emerald-50/60 text-sm text-emerald-900" data-testid="rec-apply-result">
              <div className="font-semibold mb-1">Riconciliazione applicata</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-xs">
                <div>Dipendenti allineati: <b>{applyResult.employees_matched}</b></div>
                <div>Presenze aggiornate: <b>{applyResult.attendance_updated}</b></div>
                <div>Presenze create: <b>{applyResult.attendance_created}</b></div>
                <div>Profili ricreati: <b>{applyResult.profiles_recreated}</b></div>
                <div>Giorni neutri: <b>{applyResult.neutral_days}</b></div>
                <div>Recuperi "Bo" aggiunti: <b>{applyResult.bo_recovery_added}</b></div>
                <div>Ordinari riallineati: <b>{applyResult.ordinary_realigned}</b></div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <div className="border border-emerald-200 rounded-md p-3 bg-emerald-50">
              <div className="text-xs uppercase tracking-widest text-emerald-700">Dipendenti OK</div>
              <div className="text-2xl font-mono font-bold text-emerald-900">{result.n_ok} / {result.n_matched}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-3 bg-white">
              <div className="text-xs uppercase tracking-widest text-slate-500">Totale atteso (tabulato)</div>
              <div className="text-2xl font-mono font-bold">{fmtHoursSigned(result.totals.tot_expected)}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-3 bg-white">
              <div className="text-xs uppercase tracking-widest text-slate-500">Totale calcolato (app)</div>
              <div className="text-2xl font-mono font-bold">{fmtHoursSigned(result.totals.tot_computed)}</div>
            </div>
            <div className={`border rounded-md p-3 ${Math.abs(result.totals.tot_diff_h) < 0.05 ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
              <div className="text-xs uppercase tracking-widest text-slate-700">Δ totale</div>
              <div className="text-2xl font-mono font-bold">{fmtHoursSigned(result.totals.tot_diff_h)} <span className="text-sm text-slate-500">({result.totals.tot_diff_min > 0 ? "+" : ""}{result.totals.tot_diff_min.toFixed(1)} min)</span></div>
            </div>
          </div>

          <Card className="border-slate-200 rounded-md overflow-hidden">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-widest">Dipendente</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Matricola</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Mese</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Atteso (tab.)</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Calcolato (app)</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Δ (h)</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Δ (min)</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Causa</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.employees.map((e, i) => (
                    <React.Fragment key={i}>
                      {e.not_matched && (
                        <TableRow className="bg-red-50/40">
                          <TableCell colSpan={8} className="text-red-800 font-semibold">
                            {e.employee_name} — non abbinato nell'app (matricola mancante?)
                          </TableCell>
                        </TableRow>
                      )}
                      {!e.not_matched && (
                        <TableRow className={Math.abs(e.tot_diff_h) < 0.02 ? "bg-emerald-50/40 font-semibold" : "font-semibold bg-amber-50/40"}>
                          <TableCell colSpan={2}>
                            {Math.abs(e.tot_diff_h) < 0.02 ? <CheckCircle2 size={14} className="inline text-emerald-700 mr-1" /> : <XCircle size={14} className="inline text-amber-700 mr-1" />}
                            {e.employee_name}
                          </TableCell>
                          <TableCell>Totale semestre</TableCell>
                          <TableCell className="text-right font-mono">{fmtHoursSigned(e.tot_expected)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtHoursSigned(e.tot_computed)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtHoursSigned(e.tot_diff_h)}</TableCell>
                          <TableCell className="text-right font-mono">{e.tot_diff_min > 0 ? "+" : ""}{e.tot_diff_min.toFixed(1)}</TableCell>
                          <TableCell>
                            {Math.abs(e.tot_diff_h) < 0.02
                              ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">quadratura al centesimo</Badge>
                              : <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">verifica</Badge>}
                          </TableCell>
                        </TableRow>
                      )}
                      {!e.not_matched && e.months.map((m, mi) => (
                        <TableRow key={mi} className={Math.abs(m.diff_h) > 0.005 ? "bg-amber-50/20" : "text-slate-500"}>
                          <TableCell></TableCell>
                          <TableCell className="font-mono text-xs">{e.matricola || "—"}</TableCell>
                          <TableCell className="text-xs">{MONTHS_IT[m.month - 1]} {m.year}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{fmtHoursSigned(m.expected)}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{fmtHoursSigned(m.computed)}</TableCell>
                          <TableCell className={`text-right font-mono text-sm ${Math.abs(m.diff_h) > 0.005 ? "font-bold" : ""}`}>{fmtHoursSigned(m.diff_h)}</TableCell>
                          <TableCell className={`text-right font-mono text-sm ${Math.abs(m.diff_min) > 1 ? "font-bold" : ""}`}>{m.diff_min > 0 ? "+" : ""}{m.diff_min.toFixed(1)}</TableCell>
                          <TableCell className="text-xs text-slate-700">
                            {(m.causes || []).map((c, ci) => <div key={ci}>{c}</div>)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

